import { useState } from 'react';
import GlobalStyles from '@mui/material/GlobalStyles';
import { useTheme } from '@mui/material/styles';
import { TileLayer } from 'react-leaflet';
import {
  trimbleTileUrl,
  TRIMBLE_ATTRIBUTION,
  OSM_TILE_URL,
  OSM_ATTRIBUTION,
} from './trimbleTiles';

/**
 * The basemap every map in the app draws on: Trimble's tiles, proxied by the
 * API so the PC*MILER key never reaches the browser.
 *
 * Falls back to OpenStreetMap in two cases:
 *
 *  1. There is no token to authenticate the proxy with.
 *  2. The proxy fails. This one matters — an API that hasn't been deployed with
 *     the tile route yet answers 404, and Leaflet's response to a failing tile
 *     source is a silent blank grid. A blank map reads as "the app is broken",
 *     so a few consecutive tile errors switch the layer to OSM instead. Better
 *     the wrong basemap than no map.
 *
 * The threshold is deliberately not 1: a single tile can fail transiently on a
 * flaky connection without the source being wrong.
 */
const TILE_ERROR_THRESHOLD = 4;

/**
 * Derives a dark basemap from light tiles. Trimble has no genuinely dark style
 * — its darkest non-satellite option still averages RGB 204 — so a dark page
 * would otherwise carry a glaring white rectangle. `invert` flips the
 * luminance and `hue-rotate(180deg)` puts the hues back, so water stays blue
 * instead of turning orange.
 */
const TILE_DARK_STYLES = {
  '.itow-tile-dark': {
    filter: 'invert(1) hue-rotate(180deg) brightness(0.92) contrast(0.9) saturate(0.75)',
  },
} as const;

export function BaseTileLayer() {
  const [failed, setFailed] = useState(false);
  // Counted in a ref-like state updater rather than read directly — the render
  // only cares about `failed`.
  const [, setErrors] = useState(0);
  const theme = useTheme();
  const mode = theme.palette.mode === 'dark' ? 'dark' : 'light';
  const trimble = trimbleTileUrl(mode);
  const useTrimble = trimble !== null && !failed;

  // The dark treatment is applied to whichever basemap is in use, not just
  // Trimble's. Callers draw their overlays assuming "dark mode means dark
  // tiles" — leave the fallback light and a route stroke picked for a dark
  // basemap turns into pale blue on a pale map.
  const darkClass = mode === 'dark' ? 'itow-tile-dark' : undefined;

  if (!useTrimble) {
    return (
      <>
        <GlobalStyles styles={TILE_DARK_STYLES} />
        <TileLayer url={OSM_TILE_URL} attribution={OSM_ATTRIBUTION} className={darkClass} />
      </>
    );
  }

  return (
    <>
      <GlobalStyles styles={TILE_DARK_STYLES} />
    <TileLayer
      // Remount on fallback so Leaflet drops the dead layer rather than
      // retaining its cached error state.
      key={`trimble-${mode}`}
      url={trimble}
      attribution={TRIMBLE_ATTRIBUTION}
      className={darkClass}
      eventHandlers={{
        tileerror: () => {
          setErrors((n) => {
            const next = n + 1;
            if (next >= TILE_ERROR_THRESHOLD) setFailed(true);
            return next;
          });
        },
      }}
    />
    </>
  );
}
